import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Icons, Modal, Table, UnavailableFeatureMessage } from '@components/common';
import PageTitle from '@components/common/PageTitle';

import useQueryNetworkElementConfigurationMiddleware
	from '@hooks/networkElementConfigurations/useQueryNetworkElementConfigurationMiddleware';
import useQueryNetworkElementConfigurations
	from '@hooks/networkElementConfigurations/useQueryNetworkElementConfigurations';
import useQueryNetworkElementConfigurationStatuses
	from '@hooks/networkElementConfigurations/useQueryNetworkElementConfigurationStatuses';
import useQueryNetworkElementConfigurationTypes
	from '@hooks/networkElementConfigurations/useQueryNetworkElementConfigurationTypes';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { auth, configurationManagement } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { clearJSMemoryHeap } from '@utils/clearJSMemoryHeap';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';
import { INetworkElementConfiguration } from '@interfaces/networkElementConfigurations/networkElementConfigurations';

import { defaultMeta, EAdminPanelPageTitles } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_WIDTH_880 } from '@constants/modals';
import { routes } from '@constants/routes';

import NetworkConfigurationModal from './NetworkConfigurationModal';
import { getColumns } from './utils';

import styles from './ManageNetworkElementConfigurations.module.scss';

const {
	adminPanel,
	cmModule,
} = routes;

const ManageNetworkElementConfigurations = () => {
	const {
		accessPermission: {
			networkOperatorCmModuleAdmin,
			networkOperatorCmModuleEditor,
		},
	} = useAppSelector(auth);

	const { selectedVendor } = useAppSelector(configurationManagement);

	const navigate = useNavigate();

	const [isOpenViewModal, setIsOpenViewModal] = useState(false);
	const [isEditMode, setIsEditMode] = useState(false);
	const [isDirtyForm, setIsDirtyForm] = useState(false);

	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	const [selectedConfigurations, setSelectedConfiguration] = useState<INetworkElementConfiguration | null>(null);
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [selectedStatus, setSelectedStatus] = useState<TSelectOption<string> | null>(null);
	const [selectedType, setSelectedType] = useState<TSelectOption<string> | null>(null);
	const [selectedMiddleware, setSelectedMiddleware] = useState<TSelectOption<string> | null>(null);

	const {
		networkElementConfigurations,
		countOfRecords,
		isLoadingNetworkElementConfigurations,
	} = useQueryNetworkElementConfigurations({
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		status: selectedStatus?.value,
		type: selectedType?.value,
	});

	const { middlewareOptions, isLoadingMiddlewareOptions } = useQueryNetworkElementConfigurationMiddleware();
	const { typesOptions, isLoadingTypesOptions } = useQueryNetworkElementConfigurationTypes();
	const { statusesOptions, isLoadingStatusesOptions } = useQueryNetworkElementConfigurationStatuses();

	const openViewModal = useCallback((config: INetworkElementConfiguration | null) => {
		if (!config) return;

		setIsOpenViewModal((prevState) => !prevState);
		setSelectedConfiguration(config);
	}, []);

	const closeViewModal = useCallback(() => {
		setIsOpenViewModal(false);
		setSelectedConfiguration(null);
		setIsEditMode(false);

		clearJSMemoryHeap();
	}, []);

	const openChangeMonitoring = (config: INetworkElementConfiguration) => {
		const url = `${adminPanel.index}${cmModule.changeMonitoring.replace(':id', config.networkElementId)}`;

		navigate(url, { state: { isRedirectedFromAdminPanel: true } });
	};

	const isViewerOnly = !networkOperatorCmModuleEditor && !networkOperatorCmModuleAdmin;

	const modalTitle = isEditMode ? 'Edit configuration' : 'View configuration';

	const isLoadingTable = isLoadingNetworkElementConfigurations
		|| isLoadingMiddlewareOptions
		|| isLoadingTypesOptions
		|| isLoadingStatusesOptions;

	const columns = getColumns({
		openModal: openViewModal,
		openChangeMonitoring,
		setIsEditMode,
		isViewer: isViewerOnly,
	});

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);

	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [page, pageSize, countOfRecords, pageHandler, pageSizeHandler]);

	const filters: TTableFilter[] = useMemo(() => [
		{
			name: 'status',
			placeholder: 'Status',
			option: selectedStatus,
			options: statusesOptions,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedStatus,
			defaultValue: '',
		},
		{
			name: 'middlewareId',
			placeholder: 'Middleware',
			option: selectedMiddleware,
			options: middlewareOptions,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedMiddleware,
			defaultValue: '',
		},
		{
			name: 'type',
			placeholder: 'Type',
			option: selectedType,
			options: typesOptions,
			minWidth: MIN_FILTER_WIDTH,
			setState: setSelectedType,
			defaultValue: '',
		},
	], [
		selectedStatus,
		selectedMiddleware,
		selectedType,
		statusesOptions,
		middlewareOptions,
		typesOptions,
	]);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	return isFeatureAvailable ? (
		<div className={styles.wrapper}>
			<PageTitle
				icon={Icons.configurationIcon()}
				title={EAdminPanelPageTitles.ManageNetworkElementConfigurations}
			/>
			{isOpenViewModal && (
				<Modal
					height='85vh'
					isConfirmDialogNeededOnClose
					isDirtyForm={isDirtyForm}
					isOpen={isOpenViewModal}
					onClose={closeViewModal}
					title={`${modalTitle} ${selectedConfigurations?.type}`}
					width={MODAL_WIDTH_880}
				>
					<NetworkConfigurationModal
						closeViewModal={closeViewModal}
						config={selectedConfigurations}
						isEditMode={isEditMode}
						onClose={closeViewModal}
						setIsDirtyForm={setIsDirtyForm}
					/>
				</Modal>
			)}
			<div className={styles.tableContainer}>
				<Table
					columns={columns}
					data={networkElementConfigurations}
					filters={filters}
					isLoading={isLoadingTable}
					isOpenFilters={!!selectedStatus}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
				/>
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default ManageNetworkElementConfigurations;
